import { DecodedToken, getEnabledCpServices } from '@climatepartner/utils';
import { AccessTokenServiceNames } from './constans';
import { DecodedTokenWithServices } from './token';

const servicesMapping: Record<string, ServiceId> = {
    'network-purchaser': 'network',
    'network-supplier': 'network',
} as const;

export type ServiceId = string;

export type ServiceLink = {
    id: ServiceId;
    url: string;
};

export type Role = Array<'network-supplier' | 'network-purchaser'>;

export const sortServiceLinks = (serviceLinks: ServiceLink[]): ServiceLink[] => {
    const sortingOrderMap: Record<string, number> = {
        fpc: 1,
        cfc: 1,
        'reduction-homepage-frontend': 2,
        fpm: 3,
        network: 4,
        'transparency-dashboard-frontend': 5,
    };
    return [...serviceLinks].sort((left, right) => {
        return sortingOrderMap[left.id] - sortingOrderMap[right.id];
    });
};

export const getAllRawEnabledServiceNames = (decodedToken: DecodedToken | null | undefined) => {
    return Object.values(decodedToken?.organization_access || {}).flatMap(({ services }) => {
        return services;
    });
};
export function extractUserServiceLinks(decodedToken: DecodedTokenWithServices): ServiceLink[] {
    const { organization_access: organizationAccess, services: urls } = decodedToken;
    if (!organizationAccess) {
        return [];
    }
    const enhancedUrls = enhanceUrlsWithCCFSolutions(urls);
    const enabledServices = Object.keys(getEnabledCpServices(enhancedUrls, organizationAccess));
    const organisationSlug = Object.keys(organizationAccess)[0];

    const value = enabledServices.reduce<ServiceLink[]>((acc, serviceId) => {
        const mappedServiceId = servicesMapping[serviceId] ?? serviceId;

        const url = enhancedUrls[mappedServiceId] ?? enhancedUrls[serviceId];

        if (!url) {
            return acc;
        }

        if (!acc.find(({ id }) => mappedServiceId === id)) {
            return [
                ...acc,
                {
                    id: mappedServiceId,
                    url: resolveUrl(mappedServiceId, url, organisationSlug),
                },
            ];
        }

        return acc;
    }, []);
    return value;
}

// TODO: Move URL resolution logic to CFC.
// Pass only the base URL and let CFC append necessary paths.
const resolveUrl = (mappedServiceId: string, url: string, organisationSlug: string): string => {
    const cfcServiceNames = Object.values(AccessTokenServiceNames) as string[];
    return cfcServiceNames.includes(mappedServiceId) ? `${url}/customer/${organisationSlug}/ccf` : url;
};

const enhanceUrlsWithCCFSolutions = (urls: Record<string, string> = {}): Record<string, string> => {
    return Object.keys(urls).reduce((acc: Record<string, string>, key: string) => {
        acc[key] = urls[key];

        if (key === 'cfc') {
            const cfcUrl = urls[key];
            acc['cfc.ccf-enterprise'] = cfcUrl;
            acc['cfc.ccf-assessment'] = cfcUrl;
            acc['cfc.ccf-employee-commuting-survey'] = cfcUrl;
        }

        return acc;
    }, {});
};

export const resolveOIDCConfig = (config?: Window['$cp']['oidc']) => {
    if (
        config === null ||
        typeof config !== 'object' ||
        !Object.prototype.hasOwnProperty.call(config, 'client_id') ||
        !Object.prototype.hasOwnProperty.call(config, 'issuer')
    ) {
        return undefined;
    }
    const resolvedRealm = config.issuer.includes('/climatepartner-admin/') ? 'admin' : 'user';
    return {
        defaultRealm: resolvedRealm,
        openidRealmConfig: {
            [resolvedRealm]: {
                clientId: config.client_id,
                url: `${config.issuer}/.well-known/openid-configuration`,
            },
        },
    };
};
